import {
  AccountActivityRow,
  GeneralLedgerRow,
  JournalEntryRecord,
  TrialBalanceRow,
} from '@/renderer/types/accounting';

export type AccountingReportTab =
  | 'trial-balance'
  | 'general-ledger'
  | 'account-activity';

export const ACCOUNTING_REPORT_TABS: Array<{
  id: AccountingReportTab;
  label: string;
}> = [
  { id: 'trial-balance', label: 'Trial Balance' },
  { id: 'general-ledger', label: 'General Ledger' },
  { id: 'account-activity', label: 'Account Activity' },
];

export const formatCurrencyValue = (value: number) =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const buildTrialBalanceCsv = (rows: TrialBalanceRow[]) =>
  [
    [
      'Account Code',
      'Account Name',
      'Type',
      'Subtype',
      'Normal Balance',
      'Total Debits',
      'Total Credits',
      'Closing Balance',
      'Journal Entries',
      'Last Entry Date',
    ],
    ...rows.map((row) => [
      row.account_code,
      row.account_name,
      row.account_type,
      row.account_subtype || '',
      row.normal_balance,
      formatCurrencyValue(row.total_debits),
      formatCurrencyValue(row.total_credits),
      formatCurrencyValue(row.closing_balance),
      row.journal_entry_count.toString(),
      row.last_entry_date || '',
    ]),
  ]
    .map((line) =>
      line.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','),
    )
    .join('\n');

export const buildGeneralLedgerCsv = (rows: GeneralLedgerRow[]) =>
  [
    [
      'Entry Date',
      'Account Code',
      'Account Name',
      'Source Module',
      'Reference Type',
      'Reference ID',
      'Memo',
      'Line Description',
      'Debit',
      'Credit',
      'Running Balance',
      'Status',
    ],
    ...rows.map((row) => [
      row.entry_date,
      row.account_code,
      row.account_name,
      row.source_module,
      row.reference_type || '',
      row.reference_id || '',
      row.memo || '',
      row.line_description || '',
      formatCurrencyValue(row.debit),
      formatCurrencyValue(row.credit),
      formatCurrencyValue(row.running_balance),
      row.status,
    ]),
  ]
    .map((line) =>
      line.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','),
    )
    .join('\n');

export const buildAccountActivityCsv = (rows: AccountActivityRow[]) =>
  [
    [
      'Entry Date',
      'Account Code',
      'Account Name',
      'Source Module',
      'Reference Type',
      'Journal Entries',
      'Journal Lines',
      'Total Debits',
      'Total Credits',
      'Net Activity',
    ],
    ...rows.map((row) => [
      row.entry_date,
      row.account_code,
      row.account_name,
      row.source_module,
      row.reference_type || '',
      row.journal_entry_count.toString(),
      row.journal_line_count.toString(),
      formatCurrencyValue(row.total_debits),
      formatCurrencyValue(row.total_credits),
      formatCurrencyValue(row.net_activity),
    ]),
  ]
    .map((line) =>
      line.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','),
    )
    .join('\n');

export const buildJournalEntriesCsv = (entries: JournalEntryRecord[]) =>
  [
    [
      'Date',
      'Memo',
      'Source',
      'Status',
      'Scope',
      'Reference Type',
      'Reference ID',
    ],
    ...entries.map((e) => [
      e.entry_date,
      e.memo ?? '',
      e.source_module,
      e.status,
      e.posting_scope,
      e.reference_type ?? '',
      e.reference_id ?? '',
    ]),
  ]
    .map((line) =>
      line.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','),
    )
    .join('\n');
